import http from '@/api/http'
import { clientsPageLimit } from '@/config/api'
import { majorToMinor } from '@/utils/majorToMinor'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface Balance {
  // Snapshot row for `current_balance` from GET `/persons/clients`.

  id: string
  // Balance record id.

  person_id: string
  // Person this balance applies to.

  amount: number
  // Monetary amount in major currency units (e.g. dollars); see backend contract if this differs.

  calculation_date: string
  // ISO 8601 datetime string (aware datetime serialized to JSON).
}

export interface ClientRole {
  // Role assigned to a client row from the persons API.

  id: number
  // Role id.

  name: string
  // Role name.

  description: string | null
  // Optional role description.
}

export interface Client {
  // One client record from GET `/persons/clients`.

  id: string
  // Client id.

  user_id: string | null
  // Linked user id when present.

  name: string
  // Display name.

  birthday: string | null
  // Birth date string when present.

  roles: ClientRole[]
  // Assigned roles.

  phone: string | null
  // Phone when present.

  email: string | null
  // Email when present.

  gender: string
  // Sex/gender string from the API.

  current_balance?: Balance | null
  // Optional balance snapshot; null or omitted when not present.

  latest_balance?: number | null
  // Balance as of the 1st of the current month plus payments made so far this
  // month (see backend `PersonRspDTO.latest_balance`); null when neither a
  // balance nor a payment was ever recorded. Non-negative means last month's
  // debt is closed; negative means it isn't yet.

  bills: unknown[]
  // Bills linked to the client; shape from backend.

  diseases: unknown[]
  // Diseases linked to the client; shape from backend.

  social_network_accounts: unknown[]
  // Social accounts; shape from backend.

  payments: unknown[]
  // Payments; shape from backend.

  start_datetime?: string | null
  // Service start datetime (ISO string) when the API includes it.

  last_visit_datetime?: string | null
  // Last visit datetime when the API includes it.

  business_service_count: number | null
  // Number of business services visited by the client.
}

/**
 * Balance in minor currency units for `MoneyCell`, or `undefined` when absent.
 * Converts `current_balance.amount` from major units using `defaultPrecision`.
 */
export function clientBalanceMinorUnits(client: Client): number | undefined {
  return majorToMinor(client.current_balance?.amount)
}

/**
 * Debt in minor currency units for `DebtCell`, or `undefined` when absent.
 * Converts `latest_balance` from major units using `defaultPrecision`.
 */
export function clientDebtMinorUnits(client: Client): number | undefined {
  return majorToMinor(client.latest_balance)
}

interface ClientsResponse extends PageMeta {
  // Paginated list payload from GET `/persons/clients`.

  items: Client[]
  // Page of clients.
}

export type ClientListFilter = 'active' | 'negative' | 'all'
// Preset filter for the clients list tab: 'active' clients with a visit inside
// the recency window, 'negative' clients with an outstanding negative
// balance, 'all' applies no filter at all.

export const ACTIVE_CLIENTS_RECENCY_MONTHS = 2
// Months of inactivity before a client drops out of the 'active' preset filter; also used to label the UI toggle.

/**
 * ISO datetime for "now minus the recency window", used as the `last_visit`
 * lower bound for the 'active' preset filter.
 */
function activeClientsSinceIso(): string {
  const cutoff = new Date()
  cutoff.setMonth(cutoff.getMonth() - ACTIVE_CLIENTS_RECENCY_MONTHS)
  return cutoff.toISOString()
}

/**
 * Builds the compact filter query params (see backend `FilterChecker`) for a
 * clients list preset. 'all' contributes no params so the endpoint behaves
 * exactly as it did before presets existed.
 */
function clientFilterParams(filter: ClientListFilter): Record<string, string> {
  if (filter === 'negative') return { balance: 'lt:0' }
  if (filter === 'active') return { last_visit: `gte:${activeClientsSinceIso()}` }
  return {}
}

/**
 * Fetches a page of clients from the API.
 * @param cursor - Optional cursor for the next page (from previous response next_cursor). Omit for first page.
 * @param search - Optional case-insensitive substring match on the client name (compact `name=like:` filter).
 * @param filter - Preset filter; defaults to 'all' so existing callers (client pickers in payment/service
 * forms) keep seeing every client. `ClientsTab` passes its own segmented-control selection explicitly.
 * Base URL from @/config/api (VITE_API_BASE_URL).
 */
export async function fetchClients(
  cursor?: number,
  search?: string,
  filter: ClientListFilter = 'all',
): Promise<ClientsResponse> {
  const trimmedSearch = search?.trim()
  const params = {
    ...cursorParams(clientsPageLimit, cursor),
    ...clientFilterParams(filter),
    ...(trimmedSearch ? { name: `like:${trimmedSearch}` } : {}),
  }
  const { data } = await http.get<ClientsResponse>('/persons/clients', { params })
  if (import.meta.env.DEV) {
    console.log('[fetchClients] GET /persons/clients response', data)
  }
  return data
}

/**
 * Fetches a page of clients whose latest balance is negative, via the
 * dedicated GET `/persons/clients/negative-balance` endpoint (gated by its own
 * accountant/admin/owner-only permission on the backend, separate from the
 * general clients-view permission). Same cursor/limit contract as `fetchClients`.
 */
export async function fetchClientsWithNegativeBalance(cursor?: number): Promise<ClientsResponse> {
  const { data } = await http.get<ClientsResponse>('/persons/clients/negative-balance', {
    params: cursorParams(clientsPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log(
      '[fetchClientsWithNegativeBalance] GET /persons/clients/negative-balance response',
      data,
    )
  }
  return data
}

interface ClientsByIdsResponse extends PageMeta {
  // Response payload from POST `/persons/clients/by-ids`.
  // `next_cursor` is always null and `has_next_page` is always false (single-shot lookup).

  items: Client[]
  // Clients found for the requested ids; ids with no matching client (or without the `client` role) are simply omitted.

  limit: number
  // Echoed limit (backend sizes this to the input).
}

/**
 * Resolves clients by ids in a single batch call.
 *
 * POST `/persons/clients/by-ids` returns only persons with the `client` role;
 * any requested id whose person is unknown or lacks that role is silently
 * omitted from `items`. Callers should fall back to a placeholder label
 * (e.g. `Person #<id>`) for missing entries rather than assume completeness.
 *
 * If **none** of the requested ids map to a client, the backend responds with
 * HTTP 403 + `error_code = BACKEND_10029` (`CLIENTS_BY_IDS_NO_CLIENTS_FOUND`);
 * the shared axios response interceptor then navigates to the dedicated error
 * page, so this function will not resolve in that case.
 *
 * Short-circuits to an empty response for an empty input so callers can
 * safely chain it with reactive id lists without an extra conditional.
 */
export async function fetchClientsByIds(ids: readonly string[]): Promise<ClientsByIdsResponse> {
  if (ids.length === 0) {
    return {
      items: [],
      items_number: 0,
      limit: 0,
      next_cursor: null,
      has_next_page: false,
      order: 'DESCENDING',
    }
  }
  const { data } = await http.post<ClientsByIdsResponse>('/persons/clients/by-ids', {
    ids: Array.from(ids),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchClientsByIds] POST /persons/clients/by-ids response', data)
  }
  return data
}

/**
 * Deletes a person (client) by id.
 * @param personId - The id of the person to delete.
 */
export async function deleteClient(personId: string): Promise<void> {
  await http.delete(`/persons/${personId}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteClient] DELETE /persons/${personId}`)
  }
}

export interface CreateClientPayload {
  // Request body for POST `/persons` (create a new client).

  name: string
  // Full display name (required).

  gender: 'MALE' | 'FEMALE'
  // Sex/gender (required).

  birthday: { day: number; month: number; year: number | null } | null
  // Birth date — null when omitted; day and month are required if provided.

  role_ids: number[]
  // IDs of roles to assign (at least one required).

  phone: string | null
  // Phone number; at least one of phone/email must be non-null.

  email: string | null
  // Email address; at least one of phone/email must be non-null.

  user_id?: string | null
  // Optional UUID of a linked user account.

  start_datetime: string
  // ISO 8601 datetime marking when the client's relationship started.

  social_network_accounts: Array<{
    social_network_id: number
    // ID from GET /social-networks.

    username?: string
    // Handle (e.g. @user) on the network.

    email?: string
    phone?: string
  }>
  // Social network handles — empty array when none are provided.
}

export async function createClient(payload: CreateClientPayload): Promise<Client> {
  const { data } = await http.post<Client>('/persons', payload)
  if (import.meta.env.DEV) {
    console.log('[createClient] POST /persons response', data)
  }
  return data
}

export async function fetchClient(id: string): Promise<Client> {
  // Single-shot lookup — no pagination. Errors propagate to TanStack Query.
  const { data } = await http.get<Client>(`/persons/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[fetchClient] GET /persons/${id} response`, data)
  }
  return data
}

export interface UpdateClientPayload {
  name: string
  birthday: { day: number; month: number; year: number | null } | null
  phone: string | null
  email: string | null
  gender: 'MALE' | 'FEMALE'
  start_datetime: string
  role_ids: number[]
  social_network_accounts: Array<{
    social_network_id: number
    username?: string
    email?: string
    phone?: string
  }>
}

export async function updateClient(id: string, payload: UpdateClientPayload): Promise<Client> {
  const { data } = await http.patch<Client>(`/persons/${id}`, payload)
  if (import.meta.env.DEV) console.log(`[updateClient] PATCH /persons/${id} response`, data)
  return data
}
